import { Brain, CheckCircle2, GitBranch, MessageSquarePlus, ShieldAlert, Target } from "lucide-react";

function publicText(value) {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map(publicText).filter(Boolean).join("; ");
  if (typeof value === "object") {
    return value.description || value.text || value.hypothesis || value.explanation || value.rationale || value.claim || JSON.stringify(value);
  }
  return String(value);
}

function displayLabel(value, fallback = "Behavioral reading") {
  if (!value) return fallback;
  return String(value).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().replace(/\b\w/g, (m) => m.toUpperCase());
}

export function HBMSituationDetail({
  situation,
  perspective,
  setPerspective,
  perspectiveLens,
  setPerspectiveLens,
  confidence,
  setConfidence,
  outcome,
  setOutcome,
  busy,
  onAddPerspective,
  onSynthesize,
  onOutcome,
}) {
  if (!situation) return null;
  const analysis = situation.analysis || {};
  const perspectives = situation.perspectives || [];
  const synthesis = situation.synthesis || {};
  return (
    <section className="hbm-detail" data-testid="situation-detail-container">
      <article className="hbm-social-card hbm-detail-narrative" data-testid="situation-detail-narrative-card">
        <header>
          <span>SITUATION</span>
          <b>{displayLabel(analysis.domain)}</b>
        </header>
        <p>{situation.anonymized_body || situation.body}</p>
      </article>

      <div className="hbm-detail-two">
        <article className="hbm-social-card" data-testid="private-workspace-observation-panel">
          <header><span><Target size={15} /> DIRECT OBSERVATIONS</span></header>
          {(analysis.direct_observations || []).slice(0, 4).map((item, index) => <p key={`obs-${index}`}>{publicText(item)}</p>)}
        </article>
        <article className="hbm-social-card" data-testid="private-workspace-motive-panel">
          <header><span><Brain size={15} /> INFERRED MOTIVES & DYNAMICS</span></header>
          {(analysis.inferred_motives || analysis.hypotheses || []).slice(0, 4).map((item, index) => <p key={`motive-${index}`}>{publicText(item)}</p>)}
        </article>
      </div>

      <article className="hbm-social-card" data-testid="situation-detail-claims-panel">
        <header><span><GitBranch size={15} /> TESTABLE CLAIMS & PREDICTIONS</span><b>Provisional</b></header>
        <div className="hbm-detail-two compact">
          <div>{(analysis.testable_claims || []).slice(0, 4).map((item, index) => <p key={`claim-${index}`}><b>Claim:</b> {publicText(item)}</p>)}</div>
          <div>{(analysis.predictions || []).slice(0, 4).map((item, index) => <p key={`pred-${index}`}><b>Prediction:</b> {publicText(item)}</p>)}</div>
        </div>
      </article>

      <article className="hbm-social-card" data-testid="situation-detail-perspectives-panel">
        <header><span><MessageSquarePlus size={15} /> STRUCTURED PERSPECTIVES</span><b>{perspectives.length ? `${perspectives.length} added` : "Invite disagreement"}</b></header>
        <div className="hbm-perspective-editor" data-testid="perspective-editor-container">
          <select value={perspectiveLens} onChange={(e) => setPerspectiveLens(e.target.value)} data-testid="perspective-editor-lens-select">
            <option>Alternative explanation</option>
            <option>Evidence challenge</option>
            <option>Incentive reading</option>
            <option>Power / dependency reading</option>
            <option>Prediction</option>
          </select>
          <textarea value={perspective} onChange={(e) => setPerspective(e.target.value)} placeholder="Add a reasoned perspective, counterargument, or prediction…" data-testid="perspective-editor-textarea" />
          <label>
            Confidence
            <input type="range" min="0" max="100" value={confidence} onChange={(e) => setConfidence(Number(e.target.value))} data-testid="perspective-editor-confidence-slider" />
            <small>{confidence}%</small>
          </label>
          <button type="button" onClick={onAddPerspective} disabled={busy || perspective.trim().length < 8} data-testid="perspective-editor-submit-btn">
            <MessageSquarePlus size={15} /> Add Perspective
          </button>
        </div>
        <div className="hbm-perspective-list" data-testid="situation-detail-perspective-list">
          {perspectives.slice(0, 6).map((item) => (
            <section key={item.id}>
              <b>{displayLabel(item.analysis?.cluster, "Perspective")}</b>
              <p>{item.body}</p>
            </section>
          ))}
        </div>
      </article>

      <div className="hbm-detail-two">
        <article className="hbm-social-card" data-testid="situation-detail-synthesis-panel">
          <header><span><ShieldAlert size={15} /> SYNTHESIS</span><b data-testid="situation-detail-minority-view-badge">Strong minority views protected</b></header>
          <p>{synthesis.synthesis || analysis.synthesis_preview || "Add perspectives to generate a social synthesis."}</p>
          <p><b>Counterargument:</b> {synthesis.strongest_counterargument || "No explicit counterargument has been added yet."}</p>
          <button type="button" onClick={onSynthesize} disabled={busy} data-testid="situation-detail-synthesize-btn"><Brain size={15} /> Update Synthesis</button>
        </article>
        <article className="hbm-social-card" data-testid="situation-detail-outcome-panel">
          <header><span><CheckCircle2 size={15} /> OUTCOME</span><b>{situation.outcome ? "Reported" : "Unresolved"}</b></header>
          <textarea value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="Report what actually happened, with confidence and evidence if available…" data-testid="situation-detail-outcome-input" />
          <button type="button" onClick={onOutcome} disabled={busy || outcome.trim().length < 8} data-testid="situation-detail-outcome-report-btn"><CheckCircle2 size={15} /> Report Outcome</button>
        </article>
      </div>
    </section>
  );
}